import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { PaymentMethod, SavePaymentMethodRequest } from '../../types/billing';
import { asArray, isRecord } from '../payloadGuards';
import {
  fetchPaymentMethods as fetchPaymentMethodsService,
  deletePaymentMethod as deletePaymentMethodService,
  setDefaultPaymentMethod as setDefaultPaymentMethodService,
} from '../../services/paymentMethodService';
import api from '../api';

// Types for payment intents
export interface CreatePaymentIntentRequest {
  priceId: string; // Stripe price ID
  productId: string; // Stripe product ID
  userId: string; // Cognito sub (userId) to retrieve payment methods
  customerId?: string; // Stripe customer ID
  practiceId: string; // Practice ID
  clinicianIds: string[]; // Array of MongoDB _id values
  clinicianNames: string; // Comma-separated clinician names
  couponCode?: string; // Optional coupon code
  paymentMethodId?: string; // Optional payment method ID
  billingEmail?: string; // Account email; backend must set it on the Stripe customer
}

export interface PaymentIntentResponse {
  clientSecret: string; // Required for Payment Intent flow
  paymentIntentId?: string;
}

// Types
export interface Transaction {
  id: string;
  amount: number;
  currency: string;
  status: string;
  description?: string;
  createdAt: string;
  practiceId: string;
  [key: string]: any;
}

interface BillingState {
  paymentMethods: PaymentMethod[];
  selectedPaymentMethod: PaymentMethod | null;
  isLoading: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  isCreatingPaymentIntent: boolean;
  isLoadingTransactions: boolean;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  transactionError: string | null;
  paymentIntent: PaymentIntentResponse | null;
  transactions: Transaction[];
}

// Initial state
const initialState: BillingState = {
  paymentMethods: [],
  selectedPaymentMethod: null,
  isLoading: false,
  isSaving: false,
  isDeleting: false,
  isCreatingPaymentIntent: false,
  isLoadingTransactions: false,
  status: 'idle',
  error: null,
  transactionError: null,
  paymentIntent: null,
  transactions: [],
};

// Async thunks
export const savePaymentMethod = createAsyncThunk(
  'billing/savePaymentMethod',
  async (request: SavePaymentMethodRequest, { rejectWithValue }) => {
    try {

      // Use the api instance which handles auth headers and CORS automatically
      const response = await api.post('/payment', request);


      // Parse the response - body might be a stringified JSON
      let parsedData: any;

      if (response.data?.body) {
        // If body is a string, parse it
        if (typeof response.data.body === 'string') {
          try {
            parsedData = JSON.parse(response.data.body);
          } catch (parseError) {
            console.error('❌ Error parsing body string:', parseError);
            return rejectWithValue('Failed to parse response body');
          }
        } else {
          // Body is already an object
          parsedData = response.data.body;
        }
      } else {
        // No body field, use response.data directly
        parsedData = response.data;
      }


      // Extract paymentMethod from parsed data
      let paymentMethod: PaymentMethod;

      if (parsedData.paymentMethod) {
        paymentMethod = parsedData.paymentMethod;
      } else if (parsedData.id || parsedData.stripePaymentMethodId) {
        // The parsed data itself is the payment method
        paymentMethod = parsedData as PaymentMethod;
      } else if (parsedData.success && parsedData.paymentMethod) {
        // Response has success flag and paymentMethod nested
        paymentMethod = parsedData.paymentMethod;
      } else {
        console.error('❌ Invalid response format. Parsed data:', parsedData);
        return rejectWithValue('Invalid response format from server');
      }

      return paymentMethod;
    } catch (error: any) {
      console.error('❌ Error saving payment method:', error);

      // Handle Axios errors
      if (error.response) {
        // Server responded with error status
        const errorData = error.response.data;
        const errorMessage = errorData?.error || errorData?.message || `Failed to save payment method: ${error.response.status}`;
        console.error('❌ Error response:', errorData);
        return rejectWithValue(errorMessage);
      } else if (error.request) {
        // Request was made but no response received (network/CORS error)
        console.error('❌ No response received. Possible CORS or network error.');
        return rejectWithValue('Network error: Could not reach server. Check CORS configuration.');
      } else {
        // Something else happened
        return rejectWithValue(error.message || 'Failed to save payment method');
      }
    }
  }
);

export const fetchPaymentMethods = createAsyncThunk(
  'billing/fetchPaymentMethods',
  async (
    { userId }: { userId: string },
    { rejectWithValue }
  ) => {
    try {
      if (!userId) {
        return rejectWithValue('userId is required to fetch payment methods');
      }


      // Build query parameters - only use userId
      const params = new URLSearchParams();
      params.append('userId', userId);

      const url = `/payment?${params.toString()}`;

      const response = await api.get(url);


      // Parse the response - body might be a stringified JSON
      let parsedData: PaymentMethod[] | { paymentMethods?: PaymentMethod[] };

      if (response.data?.body) {
        if (typeof response.data.body === 'string') {
          try {
            parsedData = JSON.parse(response.data.body);
          } catch (parseError) {
            console.error('❌ Error parsing body string:', parseError);
            return rejectWithValue('Failed to parse response body');
          }
        } else {
          parsedData = response.data.body;
        }
      } else {
        parsedData = response.data;
      }


      // Handle different response formats
      let paymentMethods: PaymentMethod[];

      if (Array.isArray(parsedData)) {
        paymentMethods = parsedData;
      } else if (parsedData.paymentMethods && Array.isArray(parsedData.paymentMethods)) {
        paymentMethods = parsedData.paymentMethods;
      } else {
        console.warn('⚠️ Unexpected response format:', parsedData);
        paymentMethods = [];
      }

      return paymentMethods;
    } catch (error: any) {
      console.error('❌ Error fetching payment methods:', error);

      if (error.response) {
        const errorData = error.response.data;
        const errorMessage = errorData?.error || errorData?.message || `Failed to fetch payment methods: ${error.response.status}`;
        console.error('❌ Error response:', errorData);
        return rejectWithValue(errorMessage);
      } else if (error.request) {
        console.error('❌ No response received. Possible network error.');
        return rejectWithValue('Network error: Could not reach server.');
      } else {
        return rejectWithValue(error.message || 'Failed to fetch payment methods');
      }
    }
  }
);

export const deletePaymentMethod = createAsyncThunk(
  'billing/deletePaymentMethod',
  async (paymentMethodId: string, { rejectWithValue }) => {
    try {
      const success = await deletePaymentMethodService(paymentMethodId);
      if (!success) {
        return rejectWithValue('Failed to delete payment method');
      }
      return paymentMethodId;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete payment method');
    }
  }
);

export const setDefaultPaymentMethod = createAsyncThunk(
  'billing/setDefaultPaymentMethod',
  async (
    {
      paymentMethodId,
      userId,
    }: {
      paymentMethodId: string;
      userId: string;
    },
    { rejectWithValue }
  ) => {
    try {
      if (!userId) {
        return rejectWithValue('userId is required to set default payment method');
      }
      const success = await setDefaultPaymentMethodService(
        paymentMethodId,
        undefined, // practiceId removed
        userId
      );
      if (!success) {
        return rejectWithValue('Failed to set default payment method');
      }
      return { paymentMethodId, userId };
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to set default payment method'
      );
    }
  }
);

export const fetchTransactions = createAsyncThunk(
  'billing/fetchTransactions',
  async (
    { practiceId, filters, page, limit }: {
      practiceId: string;
      filters?: Record<string, string>;
      page?: number;
      limit?: number;
    },
    { rejectWithValue }
  ) => {
    try {
      if (!practiceId) {
        return rejectWithValue('practiceId is required to fetch transactions');
      }

      const params = new URLSearchParams({ practiceId });
      if (filters) {
        Object.entries(filters).forEach(([k, v]) => params.append(k, v));
      }
      if (page != null) params.append('page', String(page));
      if (limit != null) params.append('limit', String(limit));

      const response = await api.get(`/transactions?${params.toString()}`);

      // Normalise Lambda API Gateway envelope
      const raw = response.data;
      if (raw?.body) {
        const parsed = typeof raw.body === 'string' ? JSON.parse(raw.body) : raw.body;
        return (parsed.transactions ?? parsed) as Transaction[];
      }
      return (raw.transactions ?? raw) as Transaction[];
    } catch (error: any) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to fetch transactions';
      return rejectWithValue(msg);
    }
  }
);

// Create payment intent for invitation billing
export const createInvitationPaymentIntent = createAsyncThunk(
  'billing/createInvitationPaymentIntent',
  async (request: CreatePaymentIntentRequest, { rejectWithValue }) => {
    try {
      // Use the api instance which handles auth headers and CORS automatically
      const response = await api.post('/stripe/create-invitation-payment-intent', request);


      // Parse the response - handle different possible response structures
      let parsedData: PaymentIntentResponse;

      // Case 1: response.data is a string (entire response is stringified)
      if (typeof response.data === 'string') {
        try {
          const parsed = JSON.parse(response.data);
          // If parsed has a body field, use that
          parsedData = parsed.body || parsed;
        } catch (parseError) {
          console.error('❌ Error parsing stringified response:', parseError);
          return rejectWithValue('Failed to parse payment intent response');
        }
      }
      // Case 2: response.data has a body field (Lambda API Gateway format)
      else if (response.data?.body) {
        // If body is a string, parse it
        if (typeof response.data.body === 'string') {
          try {
            parsedData = JSON.parse(response.data.body);
          } catch (parseError) {
            console.error('❌ Error parsing payment intent body string:', parseError);
            return rejectWithValue('Failed to parse payment intent response');
          }
        } else {
          // Body is already an object
          parsedData = response.data.body;
        }
      }
      // Case 3: response.data is the direct response object
      else {
        parsedData = response.data;
      }

      // Validate that clientSecret exists in the response (Payment Intent flow)
      const clientSecret = parsedData?.clientSecret
        || (parsedData as any)?.client_secret
        || (parsedData as any)?.data?.clientSecret
        || (parsedData as any)?.data?.client_secret;

      if (!clientSecret) {
        console.error('❌ Client secret not found in parsed response:', {
          parsedData,
          responseData: response.data,
          keys: Object.keys(parsedData || {}),
        });
        return rejectWithValue('Client secret not found in payment intent response');
      }

      // Ensure clientSecret is in the expected format
      const normalizedData: PaymentIntentResponse = {
        ...parsedData,
        clientSecret: clientSecret,
      };


      return normalizedData;
    } catch (error: any) {
      console.error('❌ Error creating payment intent:', error);

      // Handle Axios errors
      if (error.response) {
        // Server responded with error status
        const errorData = error.response.data;
        const errorMessage = errorData?.error || errorData?.message || `Failed to create payment intent: ${error.response.status}`;
        console.error('❌ Error response:', errorData);
        return rejectWithValue(errorMessage);
      } else if (error.request) {
        // Request was made but no response received (network/CORS error)
        console.error('❌ No response received. Possible CORS or network error.');
        return rejectWithValue('Network error: Could not reach server. Check CORS configuration.');
      } else {
        // Something else happened
        return rejectWithValue(error.message || 'Failed to create payment intent');
      }
    }
  }
);

// Slice
const billingSlice = createSlice({
  name: 'billing',
  initialState,
  reducers: {
    setSelectedPaymentMethod: (state, action: PayloadAction<PaymentMethod | null>) => {
      state.selectedPaymentMethod = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetBillingState: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    // Save payment method
    builder
      .addCase(savePaymentMethod.pending, (state) => {
        state.isSaving = true;
        state.status = 'loading';
        state.error = null;
      })
      .addCase(savePaymentMethod.fulfilled, (state, action) => {
        state.isSaving = false;
        state.status = 'succeeded';
        // Everything below dereferences the payload, so bail before it throws.
        if (!isRecord(action.payload)) return;
        // Check if payment method already exists
        const existingIndex = state.paymentMethods.findIndex(
          (pm) => pm.id === action.payload.id
        );
        if (existingIndex >= 0) {
          state.paymentMethods[existingIndex] = action.payload;
        } else {
          state.paymentMethods.push(action.payload);
        }
        // If this is set as default, unset others
        if (action.payload.isDefault) {
          state.paymentMethods = state.paymentMethods.map((pm) =>
            pm.id === action.payload.id
              ? pm
              : { ...pm, isDefault: false }
          );
        }
      })
      .addCase(savePaymentMethod.rejected, (state, action) => {
        state.isSaving = false;
        state.status = 'failed';
        state.error = action.payload as string;
      });

    // Fetch payment methods
    builder
      .addCase(fetchPaymentMethods.pending, (state) => {
        state.isLoading = true;
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchPaymentMethods.fulfilled, (state, action) => {
        state.isLoading = false;
        state.status = 'succeeded';
        state.paymentMethods = asArray(action.payload);
      })
      .addCase(fetchPaymentMethods.rejected, (state, action) => {
        state.isLoading = false;
        state.status = 'failed';
        state.error = action.payload as string;
      });

    // Delete payment method
    builder
      .addCase(deletePaymentMethod.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deletePaymentMethod.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.paymentMethods = state.paymentMethods.filter(
          (pm) => pm.id !== action.payload
        );
        if (state.selectedPaymentMethod?.id === action.payload) {
          state.selectedPaymentMethod = null;
        }
      })
      .addCase(deletePaymentMethod.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload as string;
      });

    // Set default payment method
    builder
      .addCase(setDefaultPaymentMethod.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(setDefaultPaymentMethod.fulfilled, (state, action) => {
        state.isSaving = false;
        // Set the specified payment method as default and unset others
        state.paymentMethods = state.paymentMethods.map((pm) => ({
          ...pm,
          isDefault: pm.id === action.payload.paymentMethodId,
        }));
      })
      .addCase(setDefaultPaymentMethod.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload as string;
      });

    // Create payment intent
    builder
      .addCase(createInvitationPaymentIntent.pending, (state) => {
        state.isCreatingPaymentIntent = true;
        state.status = 'loading';
        state.error = null;
      })
      .addCase(createInvitationPaymentIntent.fulfilled, (state, action) => {
        state.isCreatingPaymentIntent = false;
        state.status = 'succeeded';
        state.paymentIntent = action.payload;
      })
      .addCase(createInvitationPaymentIntent.rejected, (state, action) => {
        state.isCreatingPaymentIntent = false;
        state.status = 'failed';
        state.error = action.payload as string;
      });

    // Fetch transactions
    builder
      .addCase(fetchTransactions.pending, (state) => {
        state.isLoadingTransactions = true;
        state.transactionError = null;
      })
      .addCase(fetchTransactions.fulfilled, (state, action) => {
        state.isLoadingTransactions = false;
        state.transactions = asArray(action.payload);
      })
      .addCase(fetchTransactions.rejected, (state, action) => {
        state.isLoadingTransactions = false;
        state.transactionError = action.payload as string;
      });
  },
});

// Export actions
export const {
  setSelectedPaymentMethod,
  clearError,
  resetBillingState,
} = billingSlice.actions;

// Export selectors
export const selectPaymentMethods = (state: { billing: BillingState }) =>
  state.billing.paymentMethods;
export const selectSelectedPaymentMethod = (state: { billing: BillingState }) =>
  state.billing.selectedPaymentMethod;
export const selectDefaultPaymentMethod = (state: { billing: BillingState }) =>
  state.billing.paymentMethods.find((pm) => pm.isDefault) || null;
export const selectBillingLoading = (state: { billing: BillingState }) =>
  state.billing.isLoading;
export const selectBillingSaving = (state: { billing: BillingState }) =>
  state.billing.isSaving;
export const selectBillingError = (state: { billing: BillingState }) =>
  state.billing.error;
export const selectIsCreatingPaymentIntent = (state: { billing: BillingState }) =>
  state.billing.isCreatingPaymentIntent;
export const selectPaymentIntent = (state: { billing: BillingState }) =>
  state.billing.paymentIntent;
export const selectTransactions = (state: { billing: BillingState }) =>
  state.billing.transactions;
export const selectIsLoadingTransactions = (state: { billing: BillingState }) =>
  state.billing.isLoadingTransactions;
export const selectTransactionError = (state: { billing: BillingState }) =>
  state.billing.transactionError;

// Export reducer
export default billingSlice.reducer;
